import { Attachment } from "@dune/types";
import { create } from "zustand";

interface ComposerMode {
	type: "new" | "reply" | "replyAll" | "forward";
	initialData?: {
		to: { value: string; label: string }[];
		cc: { value: string; label: string }[];
		subject: string;
		content: string;
		attachments?: Attachment[];
	};
	draftId?: string;
}

interface EmailState {
	selectedEmailId: string;
	activeTab: string;
	isComposerOpen: boolean;
	composerMode: ComposerMode;
	composerDirty: boolean;
	composerShouldClose: boolean;
}

interface EmailActions {
	setActiveTab: (tab: string) => void;
	selectEmail: (id: string) => void;
	clearEmailSelection: () => void;
	openComposer: (mode: ComposerMode) => void;
	closeComposer: () => void;
	setComposerDirty: (dirty: boolean) => void;
	setComposerShouldClose: (shouldClose: boolean) => void;
	clearComposerState: () => void;
}

export interface EmailStore extends EmailState {
	actions: EmailActions;
}

export const useEmailStore = create<EmailStore>((set) => {
	const state: EmailState = {
		selectedEmailId: "",
		activeTab: "communications",
		isComposerOpen: false,
		composerMode: { type: "new" },
		composerDirty: false,
		composerShouldClose: false,
	};
	const actions: EmailActions = {
		setActiveTab: (tab: string) => set({ activeTab: tab }),
		selectEmail: (id: string) =>
			set({
				selectedEmailId: id,
				isComposerOpen: false,
				composerMode: { type: "new" },
				composerDirty: false,
			}),
		clearEmailSelection: () => set({ selectedEmailId: "" }),
		openComposer: (mode: ComposerMode) =>
			set({
				composerMode: mode,
				isComposerOpen: true,
				selectedEmailId: mode.draftId || "",
				composerDirty: false,
				composerShouldClose: false,
			}),
		closeComposer: () =>
			set({
				isComposerOpen: false,
				composerMode: { type: "new" },
				composerDirty: false,
				composerShouldClose: false,
			}),
		setComposerDirty: (dirty: boolean) => set({ composerDirty: dirty }),
		setComposerShouldClose: (shouldClose: boolean) =>
			set({ composerShouldClose: shouldClose }),
		clearComposerState: () =>
			set({
				selectedEmailId: "",
				activeTab: "communications",
				isComposerOpen: false,
				composerMode: { type: "new" },
				composerDirty: false,
				composerShouldClose: false,
			}),
	};
	return { ...state, actions };
});

export const useSelectedEmailId = () =>
	useEmailStore((state) => state.selectedEmailId);
export const useActiveEmailTab = () =>
	useEmailStore((state) => state.activeTab);
export const useIsEmailComposerOpen = () =>
	useEmailStore((state) => state.isComposerOpen);
export const useEmailComposerMode = () =>
	useEmailStore((state) => state.composerMode);
export const useIsComposerDirty = () =>
	useEmailStore((state) => state.composerDirty);
export const useComposerShouldClose = () =>
	useEmailStore((state) => state.composerShouldClose);

export const useEmailActions = () => useEmailStore((state) => state.actions);
